<template>
  <div class="osk-product-comments">
    <UikitButton
      v-if="!isAuth && !isSold"
      class="osk-product-comments__button"
      full-width
      href="/"
      :loading="authLoading"
      @click.prevent="handleAuthClick"
    >
      {{ $t('components.products.productComments.unauthorizedMessage') }}
    </UikitButton>

    <UikitButton
      v-else-if="!isSold && product.productId"
      class="osk-product-comments__button"
      color="secondary"
      full-width
      @click="showAllComments('all')"
    >
      {{ $t('components.products.productComments.writeSeller') }}
    </UikitButton>

    <div
      v-if="commentsCount"
      class="osk-product-comments__content"
    >
      <div
        v-if="isAuth && commentsCount > lastComments.length"
        class="osk-product-comments__show-all-comments"
        @click="showAllComments"
      >
        {{ t('components.products.productComments.showAllComments', {commentsCount}) }}
      </div>

      <div
        v-if="commentsCount"
        class="osk-product-comments__list"
      >
        <ProductCommentsComment
          v-for="(comment, index) in lastComments"
          :key="index"
          :comment="comment"
          :refresh="updateComments"
          @answer="answerComment"
          @image-click="openGallery"
        />
      </div>
    </div>

    <UikitDialog
      v-model="commentsDialog"
      position="right"
      full-height
      content-class="osk-product-comments__dialog"
    >
      <div
        ref="$dialogWrapper"
        class="osk-product-comments__dialog-wrapper"
      >
        <div
          ref="$alert"
          class="osk-product-comments__dialog-alert"
        >
          <div class="osk-product-comments__dialog-alert-title">
            {{ $t('components.products.productComments.dialogAlertTitle') }}
          </div>

          <text-clamp
            class="osk-product-comments__dialog-alert-text"
            :expanded="expandedText"
            :text="$t('components.products.productComments.dialogAlertText')"
            :max-lines="3"
          >
            <template #after>
              <span
                class="osk-product-comments__dialog-alert-text-toggle"
                @click="expandedText = !expandedText"
              >
                <template v-if="!expandedText">
                  {{ $t('common.more') }}
                </template>

                <!-- <template v-else>
                  {{ $t('common.close') }}
                </template> -->
              </span>
            </template>
          </text-clamp>
        </div>

        <div class="osk-product-comments__dialog-content">
          <UikitLoaderInner v-if="pending && !shadowGetComments" />

          <template v-else>
            <div class="osk-product-comments__title">
              {{ $t('components.products.productComments.title') }}
            </div>

            <ProductSeller :product="product" />

            <div
              v-if="allComments?.length"
              class="osk-product-comments__list"
            >
              <ProductCommentsComment
                v-for="(comment, index) in allComments"
                :key="index"
                :comment="comment"
                :reply-comment="replyComment"
                :refresh="updateComments"
                @answer="answerComment"
                @image-click="openGallery"
              />
            </div>
          </template>
        </div>
      </div>

      <div class="osk-product-comments__dialog-footer">
        <ProductCommentsForm
          v-if="!isSold && product.productId"
          ref="$commentsForm"
          v-model:reply-comment="replyComment"
          :product-id="product.productId"
          :product="product"
          :comment-post-mode="product.commentPostMode"
          :refresh="updateComments"
          :no-focus="noFocus"
        />
      </div>
    </UikitDialog>

    <ProductGalleryDialog
      v-model="dialogImages"
      :images="imagesList"
      :current-image="imageOpened"
    />
  </div>
</template>

<script setup lang="ts">
import TextClamp from 'vue3-text-clamp';
import { ProductDTORes, CommentDTO, ProductImageDTO } from '~/restAPI/data-contracts';
import { useApi } from '~/restAPI';
import { Comments } from '~/restAPI/Comments';
import { Catalog } from '~/restAPI/Catalog';
import { AnalyticsEvents } from '~/types/analytics';

const props = defineProps<{
  product: ProductDTORes,
}>();

const { t } = useI18n();
const authLoading = ref(false);
const expandedText = ref(false);
const commentsDialog = ref(false);
const dialogImages = ref(false);
const lastComments = ref<CommentDTO[]>(props.product.lastCommentsTree ?? []);
const replyComment = ref<CommentDTO>();
const shadowGetComments = ref(false);
const commentsCount = ref(props.product.commentsCount ?? 0);
const $dialogWrapper = ref<HTMLElement>();
const $alert = ref<HTMLElement>();
const $commentsForm = ref();
const imagesList = ref<ProductImageDTO[]>([]);
const imageOpened = ref<ProductImageDTO>();

const route = useRoute();
const { isAuth, openAuthDialog } = useAccountStore();
const { $addEvent, $setUsedesckWidgetPosition } = useNuxtApp();
const {
  openPhoneVerifyDialog,
  phoneVerifyWebPopup,
  needPhoneVerify,
} = usePhoneVerification();
const { isDesktop } = useUiBreakpoints();
const { currentCountry } = useInternational();

const cancelGetComments = 'cancelGetComments';
const cancelGetProductData = 'cancelGetProductData';
const { createApiInstance } = useApi();
const commentsApi = createApiInstance(Comments);
const catalogApi = createApiInstance(Catalog);

const isSold = computed(() => props.product.productState === 'SOLD');
const queryCommentId = computed(() => Number(route.query.commentId));

const noFocus = computed(() => phoneVerifyWebPopup.value?.productSceneContactSeller && needPhoneVerify.value);

const {
  data: allComments,
  pending,
  execute: getComments,
} = useLazyAsyncData(() => commentsApi
  .getProductCommentsTreeUsingGet(props.product.productId!, { cancelToken: cancelGetComments, format: 'json' })
  .then((data) => data.data), { immediate: false });

function reduceAllComments(items: CommentDTO[]) {
  return items.reduce((result, item) => {
    result.push(item);

    if (item.subComments) {
      result.push(...reduceAllComments(item.subComments));
    }

    return result;
  }, [] as CommentDTO[]);
}

const allCommentsWithSubComments = computed(() => reduceAllComments(allComments.value ?? []));

const { execute: getProductData } = useAsyncData(() => catalogApi.getProductUsingGet(props.product.productId!, {
  withSizeChart: true,
  currencyCode: currentCountry.value?.currency?.isoCode,
}, { cancelToken: cancelGetProductData, format: 'json' })
  .then((data) => data!.data!.data!)
  .then((res) => {
    lastComments.value = res.lastCommentsTree ?? [];
    commentsCount.value = res.commentsCount ?? 0;

    return res;
  }), { immediate: false });

function handleAuthClick() {
  openAuthDialog({ chapter: 'product_comments' });
}

async function showAllComments(type?: 'all') {
  const event = type === 'all' ? AnalyticsEvents.ProductCommentAddAll : AnalyticsEvents.ProductCommentAdd;

  $addEvent(event, { ...props.product });

  replyComment.value = undefined;
  commentsDialog.value = true;

  if (isDesktop.value) {
    $setUsedesckWidgetPosition('60px');
  }

  await getComments();

  if (phoneVerifyWebPopup.value?.productSceneContactSeller) {
    await nextTick();

    openPhoneVerifyDialog({
      chapter: 'productSceneContactSeller',
      phoneText: t('components.products.productComments.phoneVerifyDialogText'),
      secondDialog: true,
    });
  }
}

async function updateComments(comment?: CommentDTO) {
  if (shadowGetComments.value) {
    commentsApi.abortRequest(cancelGetComments);
    catalogApi.abortRequest(cancelGetProductData);
  }

  shadowGetComments.value = true;

  if (commentsDialog.value) {
    getProductData();
    await getComments();

    if (comment?.id) {
      scrollToComment(comment.id);
    }
  } else {
    await getProductData();
  }

  shadowGetComments.value = false;
}

function scrollToComment(commentId: number) {
  if (!$dialogWrapper.value) return;

  const commentEl: HTMLElement | null = $dialogWrapper.value.querySelector(`.osk-product-comment-${commentId}`);

  if (!commentEl) return;

  const content: HTMLElement | null = commentEl.querySelector('.osk-product-comment__content');
  const alertHeight = $alert.value?.offsetHeight ?? 0;

  $dialogWrapper.value.scrollTo({
    top: commentEl.offsetTop + alertHeight - $dialogWrapper.value.offsetHeight + (content?.offsetHeight ?? 0) + 24,
    behavior: 'smooth',
  });
}

async function answerComment(comment: CommentDTO) {
  $addEvent(AnalyticsEvents.ProductCommentAdd, { ...props.product });

  replyComment.value = comment;

  if (!commentsDialog.value) {
    commentsDialog.value = true;

    if (isDesktop.value) {
      $setUsedesckWidgetPosition('60px');
    }
    await getComments();
  }

  if ($dialogWrapper.value) {
    await nextTick();

    scrollToComment(comment.id!);
  }
}

function openGallery({ index, images }: {index: number, images: string[]}) {
  imagesList.value = images.map((image, i) => ({
    path: image,
    order: i + 1,
    id: i + 1,
  }));
  imageOpened.value = imagesList.value[index];
  dialogImages.value = true;
}

function setFormBlur() {
  if ($commentsForm.value) {
    $commentsForm.value.setFormBlur();
  }
}

async function answerQueryComment(commentId?: number) {
  if (!commentId) return;

  await navigateTo({ query: {} });

  if (!commentsDialog.value) {
    commentsDialog.value = true;

    if (isDesktop.value) {
      $setUsedesckWidgetPosition('60px');
    }
    await getComments();
    const comment = allCommentsWithSubComments.value?.find((c) => c.id === commentId);

    replyComment.value = comment;
  }

  if ($dialogWrapper.value) {
    await nextTick();

    scrollToComment(commentId);
  }
}

watch(() => $dialogWrapper.value, (el) => {
  if (el) {
    el.addEventListener('touchmove', setFormBlur);
  }
});

watch(() => commentsDialog.value, (value) => {
  if (value) {
    expandedText.value = false;
  } else if (isDesktop.value) {
    $setUsedesckWidgetPosition('15px');
  }
});

watch(queryCommentId, (id) => {
  if (id) {
    answerQueryComment(id);
  }
});

onMounted(() => {
  if (queryCommentId.value) {
    answerQueryComment(queryCommentId.value);
  }
});

onUnmounted(() => {
  if ($dialogWrapper.value) {
    $dialogWrapper.value.removeEventListener('touchmove', setFormBlur);
  }
});

defineExpose({
  showAllComments,
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-product-comments {
  &__title {
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
    margin-bottom: 22px;

    @include media-query(lg-and-up) {
      margin-bottom: 24px;
    }
  }

  &__show-all-comments {
    font-weight: $font-weight-semibold;
    color: $blue;
    margin-top: $indent-mlarge;
    cursor: pointer;
    display: inline-block;

    &:hover {
      text-decoration: underline;
    }
  }

  &__list {
    margin-top: $indent-mmedium;
  }

  &__dialog {
    padding-top: 50px !important;
    height: 100% !important;
    display: flex;
    flex-direction: column;
    top: 0 !important;
    bottom: auto !important;

    @include media-query(md-and-up) {
      width: 504px !important;
      padding: 89px 0 0 !important;
    }

    .osk-dialog__close {
      top: 16px;
      right: 16px;

      @include media-query(lg-and-up) {
        top: 44px;
        right: 50px;
      }
    }
  }

  &__dialog-alert {
    background: $grey-4;
    border-radius: $radius-small;
    padding: 16px 12px;
    margin: 0 0 $indent-large;
    flex-shrink: 0;

    @include media-query(md-and-up) {
      margin: 0 52px $indent-large;
    }
  }

  &__dialog-alert-title {
    @include font-style($font-size-subheading, $line-height-subheading, $font-weight-semibold);
    margin-bottom: $indent-small;
  }

  &__dialog-alert-text {
    color: $grey;
  }

  &__dialog-alert-text-toggle {
    font-weight: $font-weight-bold;
    margin-left: 4px;
    cursor: pointer;
    text-decoration: underline;

    @include media-query(lg-and-up) {
      &:hover {
        text-decoration: none;
      }
    }
  }

  &__dialog-wrapper {
    overflow-y: scroll;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__dialog-content {
    position: relative;
    height: 100%;
    width: 100%;

    @include media-query(md-and-up) {
      padding: 0 52px;
    }
  }

  &__dialog-footer {
    padding-top: 16px;

    @include media-query(md-and-up) {
      padding: 16px 52px;
    }
  }
}
</style>
